import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { Reveal, SectionLabel, PageHero, PrimaryCTA, ExpertCTA } from "@/components/ui-kit";
import { Founders, FinalCTA, Icon } from "@/components/Sections";
import { usePageMeta } from "@/hooks/usePageMeta";
import { CONFIG } from "@/config";

const PILLARS = [
  { icon: "TrendingUp", title: "Invest", line: "Mutual funds, portfolios and advanced opportunities matched to your horizon." },
  { icon: "Landmark", title: "Stabilise", line: "Bonds, FDs and REITs that give your plan a steadier floor." },
  { icon: "ShieldCheck", title: "Protect", line: "Right-sized term, health and general insurance so one event can't undo years of progress." },
  { icon: "HandCoins", title: "Borrow", line: "Loans arranged through lending partners, sized to fit your cash flow." },
  { icon: "ReceiptText", title: "Save tax", line: "Regime choices and deductions planned across the year, not in March." },
  { icon: "Target", title: "Plan", line: "Every goal dated, sized and connected to a monthly action." },
];

const PRINCIPLES = [
  ["Plan before product", "We never begin with what to buy. We begin with what you're trying to achieve, and only then discuss instruments."],
  ["Plain language", "If you can't explain an investment to a friend in two sentences, you probably shouldn't own it. We keep it simple."],
  ["No hype, no guarantees", "Markets move. We don't promise returns, rank ourselves, or chase trends — we focus on suitability and discipline."],
  ["Accountable & registered", "Our founders are AMFI-registered mutual fund distributors and BSE members. Their credentials are listed on every page."],
];

export default function AboutPage() {
  usePageMeta({
    title: "About Auxieron — Bhubaneswar's One-Partner Financial Services Firm",
    description: "Meet Auxieron Pvt. Ltd.: AMFI-registered founders bringing investments, fixed income, insurance, tax, loans and planning together for Indian families.",
    path: "/about",
  });

  return (
    <>
      <PageHero
        label="About Auxieron"
        title="One partner for"
        highlight="every financial decision."
        lead="Auxieron Pvt. Ltd. was founded in Bhubaneswar with a simple idea: most people don't need more financial products — they need one accountable partner who brings the pieces together and explains them clearly."
        testId="about-label"
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <PrimaryCTA testId="about-primary-cta" />
          <ExpertCTA testId="about-expert-cta" />
        </div>
      </PageHero>

      {/* story */}
      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.05fr]">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] border border-navy/10 shadow-hover">
              <img src="/img/about-hero.webp" alt="Abstract navy and gold growth composition representing Auxieron's approach" className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <div className="absolute bottom-5 left-5 rounded-2xl bg-white/90 px-4 py-3 backdrop-blur">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/50">Based in</p>
                <p className="font-display text-base font-bold text-navy">{CONFIG.location}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SectionLabel testId="about-story-label">Our story</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Built for people who were tired of piecing it together alone.
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-ink/70">
              <p>
                A mutual fund app here, an insurance agent there, a bank relationship manager pushing a fixed deposit, a tax scramble every March — and nobody looking at the whole picture. That fragmentation is what Auxieron set out to fix.
              </p>
              <p>
                We work as a single point of contact across investing, fixed income, protection, borrowing, tax and long-term planning. Every recommendation is made in the context of your complete financial life, not in isolation.
              </p>
              <p>
                We're proudly based in Bhubaneswar, Odisha and work with clients across India — from first-time SIP investors to families planning retirement and business owners exploring advanced opportunities.
              </p>
            </div>
            <Link to="/services" data-testid="about-services-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-royal hover:text-navy">
              Explore what we do <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* pillars */}
      <section className="bg-lightbg py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel testId="about-pillars-label">What "every solution" means</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              Six jobs. One accountable team.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <div data-testid={`about-pillar-${p.title.toLowerCase().replace(" ", "-")}`} className="group flex h-full flex-col rounded-2xl border border-navy/[0.08] bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-hover">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/[0.05] text-royal transition-colors group-hover:bg-royal group-hover:text-white">
                    <Icon name={p.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-bold text-navy">{p.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-ink/65">{p.line}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* principles */}
      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel testId="about-principles-label">How we work</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              Principles we don't bend.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-x-12 gap-y-10 md:grid-cols-2">
            {PRINCIPLES.map(([h, body], i) => (
              <Reveal key={h} delay={i * 0.08}>
                <div className="flex gap-4">
                  <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                    <BadgeCheck className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-bold text-navy">{h}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-ink/65">{body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Founders />
      <FinalCTA />
    </>
  );
}
